import { useEffect, type ReactNode } from "react";
import type { ContentItem } from "@/types/content";
import type { MediaController } from "./adapters/types";
import { useMediaAdapter } from "./adapters/useMediaAdapter";
import { MediaControllerContext } from "./context/MediaControllerContext";
import { useContentNavigationOptional } from "@/context/ContentNavigationContext";

interface MediaPlayerHostProps {
  content: ContentItem;
  signedUrl: string | null;
  children: (controller: MediaController | null, adapterProps: AdapterProps) => ReactNode;
}

export interface AdapterProps {
  ref?: React.RefObject<HTMLMediaElement>;
  containerId?: string;
  src: string;
}

export function MediaPlayerHost({ content, signedUrl, children }: MediaPlayerHostProps) {
  const type = content.type as "youtube" | "video" | "audio" | "recording";
  const src = type === "youtube" ? content.sourceUrl : signedUrl;

  const { controller, ref, containerId } = useMediaAdapter(type, src ?? null);
  const nav = useContentNavigationOptional();

  // Register cross-panel timestamp navigation: lets SourceCitationBadge and
  // chapter clicks seek the player to a specific second.
  useEffect(() => {
    if (!nav || !controller) return;
    const seekTo = (seconds: number) => {
      try {
        controller.seek(seconds);
        controller.play();
      } catch (err) {
        console.warn("[MediaPlayerHost] seek failed", err);
      }
    };
    nav.registerMediaNavigation(seekTo);
    return () => nav.registerMediaNavigation(null);
  }, [nav, controller]);

  const adapterProps: AdapterProps = {
    ref,
    containerId,
    src: src ?? "",
  };

  return (
    <MediaControllerContext.Provider value={controller}>
      {children(controller, adapterProps)}
    </MediaControllerContext.Provider>
  );
}
